import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import LoaderSpinner from 'common/LoaderSpinner/LoaderSpinner';

import styles from './InfiniteScroll.module.scss';

const InfiniteScroll = ({ children, hasMore, next, loading }) => {
  const containerRef = useRef();
  const [parentScrollRef, setParentScrollRef] = useState(null);

  const findScrollableParent = element => {
    if (!element) return null;
    let parent = element.parentElement;
    while (parent) {
      const { overflowY } = window.getComputedStyle(parent);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  };

  const scroll = useCallback(() => {
    const el = containerRef.current;
    const parent = parentScrollRef;

    if (!el && !parent) return;

    const currentEl = parent || el;

    const nearBottom = currentEl.scrollTop + currentEl.clientHeight >= currentEl.scrollHeight - 250;

    if (nearBottom && hasMore && !loading) {
      next();
    }
  }, [hasMore, next, loading, parentScrollRef]);

  useEffect(() => {
    const container = containerRef.current;
    const scrollableParent = findScrollableParent(container);

    if (scrollableParent) {
      setParentScrollRef(scrollableParent);
      scrollableParent.addEventListener('scroll', scroll);
    } else if (container) {
      container.addEventListener('scroll', scroll);
    }

    return () => {
      if (scrollableParent) {
        scrollableParent.removeEventListener('scroll', scroll);
      } else if (container) {
        container.removeEventListener('scroll', scroll);
      }
    };
  }, [scroll]);

  return (
    <section ref={containerRef} className={styles.container}>
      {children}

      {loading && <LoaderSpinner spinnerType="dots" />}
    </section>
  );
};

InfiniteScroll.propTypes = {
  children: PropTypes.node.isRequired,
  hasMore: PropTypes.bool.isRequired,
  next: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};
export default InfiniteScroll;
